import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { productsData } from '../../mocks/products';
import { useFavorites } from '../../hooks/useFavorites';
import { usePriceMonitor } from '../../hooks/usePriceMonitor';

const categories = [
  { id: 'all', name: 'All Products', icon: 'ri-apps-line', color: 'from-gray-500 to-gray-600' },
  { id: 'Phones', name: 'Phones', icon: 'ri-smartphone-line', color: 'from-teal-500 to-emerald-500' },
  { id: 'Tablets', name: 'Tablets', icon: 'ri-tablet-line', color: 'from-cyan-500 to-teal-500' },
  { id: 'Laptops', name: 'Laptops', icon: 'ri-macbook-line', color: 'from-indigo-500 to-violet-500' },
  { id: 'Monitors', name: 'Monitors', icon: 'ri-computer-line', color: 'from-rose-500 to-pink-500' },
  { id: 'Cameras', name: 'Cameras', icon: 'ri-camera-line', color: 'from-amber-500 to-orange-500' },
  { id: 'Kitchen', name: 'Kitchen', icon: 'ri-restaurant-line', color: 'from-lime-500 to-green-500' },
  { id: 'Beauty', name: 'Beauty', icon: 'ri-sparkling-line', color: 'from-pink-500 to-rose-500' },
  { id: 'Networking', name: 'Networking', icon: 'ri-router-line', color: 'from-sky-500 to-cyan-500' },
];

const brands = ['Apple', 'Sony', 'Dyson', 'LG', 'ASUS', 'Vitamix'];

const CategoriesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 3000]);
  const [sortBy, setSortBy] = useState('discount');
  const [showFilters, setShowFilters] = useState(false);

  const { isFavorite, toggleFavorite } = useFavorites();
  const { isTracked, addToTracking } = usePriceMonitor();

  const filteredProducts = useMemo(() => {
    let filtered = [...productsData];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Filter by brands
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(p => selectedBrands.includes(p.brand));
    }

    // Filter by price range
    filtered = filtered.filter(p => p.currentPrice >= priceRange[0] && p.currentPrice <= priceRange[1]);

    // Sort
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.currentPrice - b.currentPrice);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.currentPrice - a.currentPrice);
        break;
      case 'discount':
        filtered.sort((a, b) => Math.abs(b.priceChange) - Math.abs(a.priceChange));
        break;
      case 'name':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return filtered;
  }, [selectedCategory, selectedBrands, priceRange, sortBy]);

  const toggleBrand = (brand: string) => {
    setSelectedBrands(prev =>
      prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
    );
  };

  const clearFilters = () => {
    setSelectedBrands([]);
    setPriceRange([0, 3000]);
    setSelectedCategory('all');
  };

  const activeFiltersCount = selectedBrands.length + (priceRange[0] > 0 || priceRange[1] < 3000 ? 1 : 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-emerald-600 to-cyan-600 py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Browse by Category
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Find the best deals across all product categories. Filter by brand and price to discover your perfect match.
            </p>
          </div>
        </div>
      </section>

      {/* Category Pills */}
      <section className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? `bg-gradient-to-r ${cat.color} text-white shadow-lg`
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${cat.icon} text-lg`}></i>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-36">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Brand Filter */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Brand</h4>
                <div className="space-y-3">
                  {brands.map(brand => (
                    <label key={brand} className="flex items-center gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                        className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="text-gray-700 group-hover:text-gray-900">{brand}</span>
                      <span className="text-xs text-gray-400 ml-auto">
                        ({productsData.filter(p => p.brand === brand).length})
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range Filter */}
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Price Range</h4>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 mb-1 block">Min</label>
                      <input
                        type="number"
                        value={priceRange[0]}
                        onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        min={0}
                      />
                    </div>
                    <span className="text-gray-400 mt-5">-</span>
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 mb-1 block">Max</label>
                      <input
                        type="number"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        min={0}
                      />
                    </div>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={3000}
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                    className="w-full accent-teal-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>$0</span>
                    <span>$3,000</span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          {/* Mobile Filter Button */}
          <div className="lg:hidden flex items-center justify-between mb-4">
            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              <span>Filters</span>
              {activeFiltersCount > 0 && (
                <span className="bg-teal-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 text-sm cursor-pointer"
            >
              <option value="discount">Biggest Discount</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name">Name A-Z</option>
            </select>
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Header */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <p className="text-gray-600">
                <span className="font-semibold text-gray-900">{filteredProducts.length}</span> products found
              </p>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 text-sm cursor-pointer"
              >
                <option value="discount">Biggest Discount</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name">Name A-Z</option>
              </select>
            </div>

            {/* Active Filters */}
            {(selectedBrands.length > 0 || selectedCategory !== 'all') && (
              <div className="flex flex-wrap gap-2 mb-6">
                {selectedCategory !== 'all' && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm">
                    {categories.find(c => c.id === selectedCategory)?.name}
                    <button onClick={() => setSelectedCategory('all')} className="hover:text-teal-900 cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                  </span>
                )}
                {selectedBrands.map(brand => (
                  <span key={brand} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-full text-sm">
                    {brand}
                    <button onClick={() => toggleBrand(brand)} className="hover:text-teal-900 cursor-pointer">
                      <i className="ri-close-line"></i>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Products */}
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6" data-product-shop>
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className="bg-white rounded-2xl shadow-sm overflow-hidden group hover:shadow-xl transition-all duration-300"
                  >
                    <div className="relative">
                      <Link to={`/product/${product.id}`}>
                        <div className="aspect-square bg-gray-50 p-6">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      </Link>
                      {/* Discount Badge */}
                      {product.priceChange < 0 && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-red-500 to-rose-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                          {product.priceChange}%
                        </div>
                      )}
                      {/* Favorite Button */}
                      <button
                        onClick={() => toggleFavorite(product)}
                        className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                          isFavorite(product.id)
                            ? 'bg-red-500 text-white'
                            : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500'
                        }`}
                      >
                        <i className={`ri-heart-${isFavorite(product.id) ? 'fill' : 'line'} text-lg`}></i>
                      </button>
                    </div>

                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                          {product.category}
                        </span>
                        <span className="text-xs text-gray-400">{product.brand}</span>
                      </div>
                      <Link to={`/product/${product.id}`}>
                        <h3 className="font-semibold text-gray-900 mb-2 hover:text-teal-600 transition-colors cursor-pointer">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-baseline gap-2 mb-4">
                        <span className="text-2xl font-bold text-gray-900">${product.currentPrice}</span>
                        {product.highestPrice > product.currentPrice && (
                          <span className="text-sm text-gray-400 line-through">${product.highestPrice}</span>
                        )}
                      </div>

                      {/* Retailers */}
                      <div className="flex items-center gap-1 mb-4">
                        {product.retailers.slice(0, 4).map((retailer, idx) => (
                          <div
                            key={idx}
                            className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center"
                            title={retailer.name}
                          >
                            <i className={`${retailer.logo} text-xs text-gray-600`}></i>
                          </div>
                        ))}
                        {product.retailers.length > 4 && (
                          <span className="text-xs text-gray-500 ml-1">+{product.retailers.length - 4}</span>
                        )}
                      </div>

                      <button
                        onClick={() => addToTracking({
                          id: product.id,
                          name: product.name,
                          image: product.image,
                          currentPrice: product.currentPrice,
                          retailer: product.retailers[0]?.name || 'Unknown'
                        })}
                        className={`w-full py-2.5 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap ${
                          isTracked(product.id)
                            ? 'bg-teal-50 text-teal-600 border border-teal-200'
                            : 'bg-gradient-to-r from-teal-500 to-emerald-500 text-white hover:from-teal-600 hover:to-emerald-600'
                        }`}
                      >
                        {isTracked(product.id) ? (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-check-line"></i>
                            Tracking
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            <i className="ri-line-chart-line"></i>
                            Track Price
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-3xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your filters to find what you're looking for.</p>
                <button
                  onClick={clearFilters}
                  className="px-6 py-2 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 cursor-pointer whitespace-nowrap"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mobile Filter Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)}></div>
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button onClick={() => setShowFilters(false)} className="p-2 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6">
              {/* Brand Filter */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Brand</h4>
                <div className="space-y-3">
                  {brands.map(brand => (
                    <label key={brand} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => toggleBrand(brand)}
                        className="w-5 h-5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-gray-700">{brand}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-8">
                <h4 className="text-sm font-semibold text-gray-900 mb-4">Price Range</h4>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="Min"
                  />
                  <span className="text-gray-400">-</span>
                  <input
                    type="number"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="Max"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={clearFilters}
                  className="flex-1 py-3 border border-gray-200 rounded-lg font-medium text-gray-700 cursor-pointer whitespace-nowrap"
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="flex-1 py-3 bg-teal-500 text-white rounded-lg font-medium cursor-pointer whitespace-nowrap"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CategoriesPage;
